import { useState } from "react";
import api from "@/lib/api";
import { logEvent } from "@/lib/analytics";
import { PageHeader } from "@/components/ui-bits";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { format } from "date-fns";
import { toast } from "sonner";
import { FileCsv, FileXls, FilePdf, TrendUp, UsersThree, Handshake, Flag, ChartBar, ChatCircleDots, ClipboardText, Gavel, FolderSimpleUser, CalendarBlank } from "@phosphor-icons/react";

function DateField({ label, value, onChange, testId }) {
  const selected = value ? new Date(`${value}T00:00:00`) : undefined;
  return (
    <div>
      <Label className="text-xs block mb-1">{label}</Label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" className="rounded-sm w-44 justify-start font-normal" data-testid={testId}>
            <CalendarBlank size={15} className="mr-2 text-muted-foreground shrink-0" />
            {selected ? format(selected, "dd MMM yyyy") : <span className="text-muted-foreground">Pick a date</span>}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" selected={selected} onSelect={(d) => onChange(d ? format(d, "yyyy-MM-dd") : "")} initialFocus />
        </PopoverContent>
      </Popover>
    </div>
  );
}

const REPORTS = [
  { type: "accounts", title: "Accounts Export", desc: "Full account master — borrower, city, outstanding, DPD, bucket, status & assigned agent.", icon: FolderSimpleUser },
  { type: "recovery", title: "Recovery Report", desc: "All recorded payments with mode, reference and collector.", icon: TrendUp },
  { type: "agent-productivity", title: "Agent Productivity Report", desc: "Per-agent accounts, calls, connected, PTP, settlements & recovered.", icon: UsersThree },
  { type: "settlements", title: "Settlement Report", desc: "Settlement requests, amounts, waiver and approval status.", icon: Handshake },
  { type: "legal", title: "Legal Cases Report", desc: "Legal-candidate accounts, Sec 138 flags, case no, advocate & hearing.", icon: Gavel },
  { type: "ptp", title: "PTP Report", desc: "Promises to pay with amount, date and state (due/broken/fulfilled).", icon: Flag },
  { type: "dpd", title: "DPD Aging Report", desc: "DPD bucket distribution with account counts and outstanding.", icon: ChartBar },
  { type: "communication", title: "Communication Report", desc: "All calls, WhatsApp, SMS and email with delivery status.", icon: ChatCircleDots },
];

function ExportButtons({ type, suffix = "" }) {
  const download = async (format) => {
    logEvent("ExportClicked", { export_target: type, format });
    if (type === "accounts" || type === "recovery") {
      logEvent("TopAccountsExported", { report: type, format });
    }
    try {
      const res = await api.get(`/reports/${type}/export?format=${format}${suffix}`, { responseType: "blob" });
      const ext = format === "excel" ? "xlsx" : format;
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${type}_report.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      let msg = "Export failed. Please try again.";
      try {
        const data = e?.response?.data;
        if (data && typeof data.text === "function") {
          const j = JSON.parse(await data.text());
          if (j.detail) msg = j.detail;
        } else if (e?.response?.data?.detail) {
          msg = e.response.data.detail;
        }
      } catch (_) { /* keep fallback */ }
      toast.error(msg);
    }
  };
  return (
    <div className="grid grid-cols-3 gap-2 mt-4">
      <Button variant="outline" className="rounded-sm gap-1.5 w-full px-2" onClick={() => download("csv")} data-testid={`export-${type}-csv`}>
        <FileCsv size={16} weight="bold" /> CSV
      </Button>
      <Button variant="outline" className="rounded-sm gap-1.5 w-full px-2" onClick={() => download("excel")} data-testid={`export-${type}-excel`}>
        <FileXls size={16} weight="bold" /> Excel
      </Button>
      <Button variant="outline" className="rounded-sm gap-1.5 w-full px-2" onClick={() => download("pdf")} data-testid={`export-${type}-pdf`}>
        <FilePdf size={16} weight="bold" /> PDF
      </Button>
    </div>
  );
}

export default function Reports() {
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const range = `${from ? `&from_date=${from}` : ""}${to ? `&to_date=${to}` : ""}`;

  return (
    <div data-testid="reports-page">
      <PageHeader title="Reports & Analytics" subtitle="Export operational data as CSV, Excel or PDF" />

      {/* Activity Report — per-agent volume + outcomes by date range */}
      <div className="border border-primary/30 bg-primary/[0.03] p-5 mb-4" data-testid="activity-report-card">
        <div className="flex items-start gap-3">
          <ClipboardText size={28} weight="bold" className="text-primary shrink-0" />
          <div className="flex-1">
            <h3 className="font-heading font-bold text-lg">Agent Activity Report</h3>
            <p className="text-sm text-muted-foreground mt-1">Per-agent manual activity volume (calls, field visits, digital) and outcomes (PTP, payments, settlements, settled/closed, legal) — filterable by date range.</p>
            <div className="flex flex-wrap items-end gap-3 mt-4">
              <DateField label="From Date" value={from} onChange={setFrom} testId="activity-from-date" />
              <DateField label="To Date" value={to} onChange={setTo} testId="activity-to-date" />
              {(from || to) && (
                <Button variant="ghost" size="sm" className="rounded-sm text-muted-foreground" onClick={() => { setFrom(""); setTo(""); }} data-testid="activity-clear-range">Clear</Button>
              )}
            </div>
            <p className="text-xs text-muted-foreground mt-2">{from || to ? `Filtered: ${from || "start"} → ${to || "today"}` : "No date filter — exports all logged activity."}</p>
          </div>
        </div>
        <div className="max-w-md">
          <ExportButtons type="activity" suffix={range} />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {REPORTS.map((r) => (
          <div key={r.type} className="border border-border bg-card p-5 flex flex-col">
            <r.icon size={28} weight="bold" className="text-primary" />
            <h3 className="font-heading font-bold text-lg mt-3">{r.title}</h3>
            <p className="text-sm text-muted-foreground mt-1 flex-1">{r.desc}</p>
            <ExportButtons type={r.type} />
          </div>
        ))}
      </div>
    </div>
  );
}
